import { useState, useEffect, useCallback } from 'react';
import {
  collection, getDocs, getDoc, addDoc, updateDoc, doc, query, where,
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../hooks/useAuth';
import Button from '../shared/UI/Button';
import { formatDate, getFriendlyError } from '../../utils/formatters';

const DEAL_TYPES = [
  { value: 'equity', label: 'Equity' },
  { value: 'debt', label: 'Debt' },
  { value: 'revenue_share', label: 'Revenue Share' },
  { value: 'convertible_note', label: 'Convertible Note' },
];

const STATUS_LABEL = {
  draft: 'Draft',
  sent: 'Sent',
  under_negotiation: 'Under Negotiation',
  signed: 'Signed',
};

const NEXT_STATUS = {
  draft: { value: 'sent', label: 'Mark as Sent' },
  sent: { value: 'under_negotiation', label: 'Mark as Under Negotiation' },
  under_negotiation: { value: 'signed', label: 'Mark as Signed' },
};

function statusBadgeClass(status) {
  if (status === 'signed') return 'badge badge-success';
  if (status === 'under_negotiation') return 'badge badge-warning';
  if (status === 'sent') return 'badge badge-info';
  return 'badge badge-default';
}

function formatToday() {
  return new Date().toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

function fillPlaceholders(templateContent, deal, fundName) {
  const amount = deal.requested_amount != null
    ? Number(deal.requested_amount).toLocaleString()
    : '';
  const currency = deal.currency || deal.requested_currency || '';
  const map = {
    '[COMPANY_NAME]': deal.company_name || '',
    '[FUND_NAME]': fundName || '',
    '[AMOUNT]': amount,
    '[CURRENCY]': currency,
    '[DATE]': formatToday(),
    '[REFERENCE]': deal.reference_number || '',
    '[CONTACT_NAME]': deal.contact_name || '',
    '[INSTRUMENT_TERMS]': '',
  };
  let out = templateContent || '';
  Object.entries(map).forEach(([ph, val]) => {
    out = out.split(ph).join(val);
  });
  return out;
}

export default function TermSheetPanel({ deal, dealId, userRole }) {
  const { user, userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [termSheet, setTermSheet] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [fundShortName, setFundShortName] = useState('MIA');
  const [fundName, setFundName] = useState('');

  const [selectedDealType, setSelectedDealType] = useState(
    deal?.instrument_type || 'equity'
  );
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const [saving, setSaving] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [versions, setVersions] = useState([]);
  const [error, setError] = useState('');

  const performedByName =
    userProfile?.full_name || userProfile?.display_name || user?.email || '';

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // Current term sheet
      const tsSnap = await getDocs(query(
        collection(db, 'deals', dealId, 'term_sheets'),
        where('is_current', '==', true)
      ));
      const current = tsSnap.empty
        ? null
        : { id: tsSnap.docs[0].id, ...tsSnap.docs[0].data() };
      setTermSheet(current);

      // All versions for history
      const allSnap = await getDocs(
        collection(db, 'deals', dealId, 'term_sheets')
      );
      const all = allSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.version || 0) - (a.version || 0));
      setVersions(all);

      // Active templates
      const tmplSnap = await getDocs(query(
        collection(db, 'term_sheet_templates'),
        where('is_active', '==', true)
      ));
      setTemplates(tmplSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      setError(getFriendlyError(err));
    } finally {
      setLoading(false);
    }
  }, [dealId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!deal?.fund_id) return;
    getDoc(doc(db, 'funds', deal.fund_id)).then(fundDoc => {
      if (fundDoc.exists()) {
        const f = fundDoc.data();
        setFundShortName(f.short_name || f.name || 'MIA');
        setFundName(f.name || f.fund_name || f.short_name || '');
      }
    }).catch(() => {});
  }, [deal?.fund_id]);

  async function handleCreate() {
    setError('');
    const template = templates.find(t => t.deal_type === selectedDealType);
    if (!template) {
      setError(`No active ${selectedDealType.replace(/_/g, ' ')} template found. ` +
        'Ask an admin to seed or create one in Admin Config → Term Sheet Templates.');
      return;
    }
    setCreating(true);
    try {
      const filled = fillPlaceholders(template.content, deal, fundName);
      await addDoc(collection(db, 'deals', dealId, 'term_sheets'), {
        version: 1,
        is_current: true,
        deal_type: selectedDealType,
        template_id: template.id,
        status: 'draft',
        content: filled,
        created_by: user?.uid || null,
        created_by_name: performedByName,
        created_at: new Date().toISOString(),
      });
      await load();
    } catch (err) {
      setError(getFriendlyError(err));
    } finally {
      setCreating(false);
    }
  }

  async function handleSaveEdit() {
    if (!termSheet) return;
    setSaving(true);
    setError('');
    try {
      await updateDoc(doc(db, 'deals', dealId, 'term_sheets', termSheet.id), {
        content: editText,
        last_edited_at: new Date().toISOString(),
        last_edited_by: user?.uid || null,
        last_edited_by_name: performedByName,
      });
      setEditing(false);
      await load();
    } catch (err) {
      setError(getFriendlyError(err));
    } finally {
      setSaving(false);
    }
  }

  async function handleStatusUpdate() {
    if (!termSheet) return;
    const next = NEXT_STATUS[termSheet.status];
    if (!next) return;
    setUpdatingStatus(true);
    setError('');
    try {
      const updates = {
        status: next.value,
        status_updated_at: new Date().toISOString(),
        status_updated_by_name: performedByName,
      };
      if (next.value === 'sent') updates.sent_at = new Date().toISOString();
      if (next.value === 'signed') updates.signed_at = new Date().toISOString();
      await updateDoc(
        doc(db, 'deals', dealId, 'term_sheets', termSheet.id), updates
      );
      await load();
    } catch (err) {
      setError(getFriendlyError(err));
    } finally {
      setUpdatingStatus(false);
    }
  }

  async function handleDownloadPDF() {
    try {
      const html2pdf = (await import('html2pdf.js')).default;
      const el = document.getElementById('term-sheet-pdf-content');
      if (!el) { alert('Could not find term sheet content.'); return; }
      const today = new Date();
      const dateStr = today.getFullYear().toString() +
        String(today.getMonth() + 1).padStart(2, '0') +
        String(today.getDate()).padStart(2, '0');
      const cleanCompany = (deal.company_name || 'Company').replace(/[^a-zA-Z0-9]/g, '');
      const cleanFund = (fundShortName || 'MIA').replace(/[^a-zA-Z0-9]/g, '');
      const filename =
        `MIA_${cleanFund}_TermSheet_${cleanCompany}_${dateStr}_v${termSheet.version || 1}.pdf`;
      const options = {
        margin: [15, 15, 15, 15],
        filename,
        image: { type: 'jpeg', quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true, logging: false },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
      };
      await html2pdf().set(options).from(el).save();
    } catch (err) {
      console.error('Term sheet PDF error:', err);
      alert('Could not generate PDF. Please try again.');
    }
  }

  if (loading) {
    return <p className="text-muted" style={{ fontSize: 13 }}>Loading term sheet…</p>;
  }

  // ── STATE A — no term sheet yet ──
  if (!termSheet) {
    return (
      <div className="card">
        <div className="card-header"><h2>Term Sheet</h2></div>
        <div className="card-body">
          {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}
          <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', marginBottom: 16, lineHeight: 1.6 }}>
            No term sheet has been created for this deal. Select a template to get started.
          </p>
          <div className="form-field" style={{ maxWidth: 320, marginBottom: 16 }}>
            <label className="form-label">Deal Type</label>
            <select
              className="form-input"
              value={selectedDealType}
              onChange={e => setSelectedDealType(e.target.value)}
            >
              {DEAL_TYPES.map(dt => (
                <option key={dt.value} value={dt.value}>{dt.label}</option>
              ))}
            </select>
          </div>
          <Button variant="primary" loading={creating} onClick={handleCreate}>
            Create Term Sheet
          </Button>
          {templates.length === 0 && (
            <p style={{ fontSize: 12, color: 'var(--color-warning)', marginTop: 12 }}>
              No active templates found. Seed them in Admin Config → Term Sheet Templates first.
            </p>
          )}
        </div>
      </div>
    );
  }

  // ── STATE B — term sheet exists ──
  const next = NEXT_STATUS[termSheet.status];

  return (
    <div>
      {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}

      <div className="card mb-4">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h2>Term Sheet</h2>
            <span className={statusBadgeClass(termSheet.status)}>
              {STATUS_LABEL[termSheet.status] || termSheet.status}
            </span>
            <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
              v{termSheet.version || 1}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {!editing && (
              <Button size="sm" variant="secondary" onClick={() => {
                setEditText(termSheet.content || '');
                setEditing(true);
              }}>
                Edit
              </Button>
            )}
            {!editing && next && (
              <Button size="sm" variant="secondary" loading={updatingStatus} onClick={handleStatusUpdate}>
                {next.label}
              </Button>
            )}
            {!editing && (
              <button
                onClick={handleDownloadPDF}
                style={{
                  padding: '7px 14px', borderRadius: 6, border: '1px solid #1e3a5f',
                  background: '#1e3a5f', color: '#fff', cursor: 'pointer', fontSize: 13,
                }}
              >
                ↓ Download PDF
              </button>
            )}
          </div>
        </div>
        <div className="card-body">
          <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginBottom: 12 }}>
            {termSheet.last_edited_at
              ? `Last edited by ${termSheet.last_edited_by_name || '—'} on ${formatDate(termSheet.last_edited_at)}`
              : `Created by ${termSheet.created_by_name || '—'} on ${formatDate(termSheet.created_at)}`}
          </p>

          {editing ? (
            <div>
              <textarea
                value={editText}
                onChange={e => setEditText(e.target.value)}
                style={{
                  width: '100%', minHeight: 420, padding: '10px 12px',
                  border: '1px solid var(--color-border)', borderRadius: 'var(--radius)',
                  fontSize: 13, fontFamily: 'monospace', lineHeight: 1.6,
                  resize: 'vertical', boxSizing: 'border-box',
                }}
              />
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
                <Button size="sm" variant="secondary" onClick={() => setEditing(false)}>Cancel</Button>
                <Button size="sm" variant="primary" loading={saving} onClick={handleSaveEdit}>Save Changes</Button>
              </div>
            </div>
          ) : (
            <div id="term-sheet-pdf-content">
              <pre style={{
                whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 13,
                lineHeight: 1.7, margin: 0, color: 'var(--color-text)',
              }}>
                {termSheet.content}
              </pre>
            </div>
          )}
        </div>
      </div>

      {/* Version history */}
      <div className="card">
        <button
          onClick={() => setShowHistory(s => !s)}
          style={{
            width: '100%', padding: '10px 14px', background: 'none', border: 'none',
            cursor: 'pointer', textAlign: 'left', fontSize: 13, color: 'var(--color-text-secondary)',
          }}
        >
          {showHistory ? '▲ Hide' : '▼ Show'} version history ({versions.length})
        </button>
        {showHistory && (
          <div className="card-body" style={{ paddingTop: 0 }}>
            {versions.map(v => (
              <div key={v.id} style={{
                display: 'flex', justifyContent: 'space-between',
                padding: '8px 0', borderBottom: '1px solid var(--color-border-light)', fontSize: 13,
              }}>
                <span>
                  v{v.version || 1} · {STATUS_LABEL[v.status] || v.status}
                  {v.is_current && <span className="badge badge-success" style={{ marginLeft: 8 }}>Current</span>}
                </span>
                <span style={{ color: 'var(--color-text-secondary)' }}>
                  {v.created_by_name || '—'} · {formatDate(v.created_at)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
